import { doc, updateDoc } from 'firebase/firestore';
import { useForm } from 'hooks';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { firestore } from 'services';
import { Button, Input, Splash } from 'shared/Components';
import { FlexRow } from 'shared/Typhography/flex-box';
import { strings } from 'strings';
import styled from 'styled-components';
import { colors } from 'theme';
import * as yup from 'yup';

const ModalWrapper = styled.div`
  padding: 16px;
  text-align: center;
  box-sizing: border-box;
  background: #ffffff;
  box-shadow: 0px 2px 14px rgba(194, 207, 221, 0.5);
  border-radius: 10px;
  height: auto;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const XIcon = styled.span`
  cursor: pointer;
  font-size: 30px;
  padding-inline: 10px;
`;

const ButtonWrapper = styled.div`
  display: flex;
`;

interface ArtistRevenueShareModalProps {
  artistId: string;
  status: 'approved' | 'pending' | 'rejected';
  onSubmit: () => Promise<void>;
  close: () => void;
}

enum ArtistRevenueField {
  Revenue = 'revenue',
}

interface ArtistRevenueForms {
  [ArtistRevenueField.Revenue]: number;
}

const artistRevenueSchema = yup.object().shape({
  [ArtistRevenueField.Revenue]: yup
    .number()
    .transform((value) => (Number.isNaN(value) ? null : value))
    .nullable()
    .required('Revenue share is required')
    .min(0)
    .max(100),
});

export const ArtistRevenueShareModal = ({
  artistId,
  status,
  onSubmit: onConfirm,
  close,
}: ArtistRevenueShareModalProps) => {
  const [isLoading, setLoading] = useState<boolean>(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ArtistRevenueForms>({
    schema: artistRevenueSchema,
    defaultValues: {
      revenue: 0,
    },
  });

  const onSubmit = handleSubmit(async (data: ArtistRevenueForms) => {
    try {
      setLoading(true);

      const docRef = doc(firestore, `usersWeb/${artistId}`);
      await updateDoc(docRef, { revenueShare: data.revenue / 100, status: status });

      await onConfirm();

      setLoading(false);
      close();
    } catch (err) {
      /* TODO Add Translations */
      toast.error('Failed to send approval notification');
    } finally {
      setLoading(false);
    }
  });

  return (
    <ModalWrapper style={{ width: '33vw', height: 'auto' }}>
      <FlexRow justifyContent="flex-end">
        <XIcon onClick={() => !isLoading && close()}>&#10006;</XIcon>
      </FlexRow>
      <ContentWrapper>
        <div style={{ flex: 1, fontSize: 25 }}>Artist&apos;s revenue share (percentage)</div>
        <Input
          required
          placeholder={strings.placeholderName}
          name={ArtistRevenueField.Revenue}
          control={control}
          error={errors[ArtistRevenueField.Revenue]}
        />
        <ButtonWrapper>
          <div style={{ flex: 1 }}>
            <Button
              style={{ minWidth: 150 }}
              variant="cancel"
              height={40}
              disabled={isLoading}
              onClick={close}
            >
              {/* TODO Add Translations */}
              Cancel
            </Button>
          </div>
          <div style={{ flex: 1, paddingBottom: 20 }}>
            <Button
              style={{ minWidth: 150 }}
              height={40}
              disabled={isLoading || !!errors[ArtistRevenueField.Revenue]?.message}
              onClick={onSubmit}
            >
              {/* TODO Add Color to Loading component */}
              {/* TODO Take color from theme */}
              {isLoading ? (
                <Splash color={colors.white} width={20} height={20} />
              ) : (
                <>
                  {/* TODO Add translations */}
                  Confirm
                </>
              )}
            </Button>
          </div>
        </ButtonWrapper>
      </ContentWrapper>
    </ModalWrapper>
  );
};
